import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  ReceiptText,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "react-toastify";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import TransactionCreateModal from "./TransactionCreateModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";

const STATUS_OPTIONS = [
  { label: "Success", value: "success" },
  { label: "Processing", value: "processing" },
  { label: "Created", value: "created" },
  { label: "Failed", value: "failed" },
];

const TransactionsListPage: React.FC = () => {
  const [limit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { editTransaction, transactionsList } = useAppSelector(
    (state) => state.transaction
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const hasActiveFilters = Boolean(search || statusFilter);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getTransactionList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  useEffect(() => {
    if (editTransaction.data) {
      toast.success("Transaction updated successfully");
      dispatch(transactionActions.resetEditTransaction());
      getTransactionList(1);
    } else if (editTransaction.error) {
      toast.error(`Error: ${editTransaction.error}`);
    }
  }, [editTransaction]);

  const getTransactionList = (page: number) =>
    dispatch(
      transactionActions.getTransactionListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      })
    );

  const handlePageChange = (page: number) => {
    getTransactionList(page);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
    getTransactionList(1);
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Transactions | Invyntra",
        description:
          "Track and manage all transactions with detailed information.",
        keywords: "transactions, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <ReceiptText size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Transactions
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track and manage all platform transactions.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button rounded="full" onClick={() => setCreateModalOpen(true)}>
              <Plus size={16} className="mr-1" /> Add Transaction
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search transactions..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
            </div>
          )}
        </Card>

        <Card>
          {/* Table */}
          <TableV2
            columns={[
              {
                key: "transactionId",
                header: "Transaction Id",
                accessor: ({ row, key }) => (
                  <Link
                    className="underline"
                    to={`/admin/transaction/${
                      row?.transactionId || row?._id
                    }/details`}
                  >
                    #{row[key]}
                  </Link>
                ),
              },
              {
                key: "tenantId",
                header: "Tenant",
                accessor: ({ row }) => (
                  <div className="w-32">{row?.tenantId?.tenantName || "-"}</div>
                ),
              },
              {
                key: "referenceId",
                header: "Reference Id",
                accessor: ({ row }) => (
                  <div className="w-40">{row?.referenceId || "-"}</div>
                ),
              },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              {
                key: "transactionType",
                header: "Type",
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={
                      row?.[key] === "success"
                        ? "green"
                        : row?.[key] === "processing"
                        ? "yellow"
                        : row?.[key] === "created"
                        ? "blue"
                        : "red"
                    }
                  />
                ),
              },
              {
                key: "createdAt",
                header: "Created On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() =>
                        navigate(`/admin/transaction/${row._id}/edit`)
                      }
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={transactionsList?.data?.data}
            pagination={transactionsList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {createModalOpen && (
        <TransactionCreateModal
          open={createModalOpen}
          onClose={handleCreateModalClose}
        />
      )}
    </Layout>
  );
};

export default TransactionsListPage;
